'use client'

import React, { ComponentType, ReactNode } from 'react'

import { Box, Text, chakra } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Button } from '../Button'
import { UtilityButton } from '../Button/UtilityButton'
import { CloseIcon } from '../Icon/CloseIcon'
import {
  ALERT_GLYPH_SIZE,
  AlertSize,
  AlertVariant,
  FALLBACK_ALERT_VARIANT,
  alertActionStyles,
  alertBodyStyles,
  alertDescStyles,
  alertDismissStyles,
  alertIconStyles,
  alertStyles,
  alertTitleStyles,
  isAlertVariant,
  knockoutColor,
} from './Alert.styles'

export type { AlertSize, AlertVariant }

export type AlertProps = {
  variant: AlertVariant
  /**
   * Emphasized first line. Optional so compact callouts can carry a single
   * status-tinted `description` line with no heading above it.
   */
  title?: string
  description?: string
  /** Overrides the default variant glyph. Inherits the variant's icon color. */
  icon?: ReactNode
  colorScheme?: ColorScheme
  /**
   * `default` is the standalone alert card; `compact` is the inline callout
   * used inside list rows — tighter padding and a 16px glyph.
   */
  size?: AlertSize
  /**
   * Centers the glyph + copy group horizontally instead of aligning it to the
   * start. For full-bleed page banners, where the alert spans the viewport and
   * start-aligned copy would sit alone in a wide empty row.
   */
  isCentered?: boolean
  /**
   * Optional trailing action button. The Alert renders and places it; the
   * consumer owns what it does.
   */
  primaryButton?: { text: string; onClick: () => void }
  /**
   * When provided, renders a trailing close (✕) that pins to the right edge,
   * turning the alert into a dismissible full-bleed page banner (e.g. the
   * stale-incident banner). The consumer owns the dismissal — the Alert only
   * surfaces the affordance.
   */
  onDismiss?: () => void
}

// The shape renders in `currentColor` (tinted by the variant); the knockout
// glyph sits on top in the `text.on-accent` semantic (white in light on the
// status fill, near-black in dark). `chakra.*` lets the token resolve on the
// SVG `stroke`/`fill` style props.
type GlyphProps = { knockout: string; size?: number }

const SuccessGlyph = ({ knockout, size = 22 }: GlyphProps) => (
  <svg fill='none' height={size} viewBox='0 0 22 22' width={size}>
    <rect fill='currentColor' height='22' rx='4' width='22' />
    <chakra.path
      d='M4.125 12.375L8.43422 15.8125L17.1875 5.5'
      stroke={knockout}
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='2'
    />
  </svg>
)

const WarningGlyph = ({ knockout, size = 22 }: GlyphProps) => (
  <svg fill='none' height={size} viewBox='0 0 24 22' width={size}>
    <path
      d='M10.2301 1C10.9999 -0.333 12.9244 -0.333 13.6942 1L23.6535 18.25C24.4233 19.583 23.4611 21.25 21.9214 21.25H2.00286C0.463 21.25 -0.499 19.583 0.271 18.25L10.2301 1Z'
      fill='currentColor'
    />
    <chakra.circle cx='11.9622' cy='17' fill={knockout} r='1' />
    <chakra.rect
      fill={knockout}
      height='10'
      rx='1'
      width='2'
      x='10.9622'
      y='4'
    />
  </svg>
)

const ErrorGlyph = ({ knockout, size = 22 }: GlyphProps) => (
  <svg fill='none' height={size} viewBox='0 0 18.6667 18.6667' width={size}>
    <path
      d='M5.88333 1H12.7833L17.6667 5.88333V12.7833L12.7833 17.6667H5.88333L1 12.7833V5.88333L5.88333 1Z'
      fill='currentColor'
    />
    <chakra.rect
      fill={knockout}
      height='5.33333'
      rx='1'
      width='2'
      x='8.33333'
      y='5'
    />
    <chakra.circle cx='9.33333' cy='12.6667' fill={knockout} r='1' />
  </svg>
)

const InfoGlyph = ({ knockout, size = 22 }: GlyphProps) => (
  <svg fill='none' height={size} viewBox='0 0 22 22' width={size}>
    <circle cx='11' cy='11' fill='currentColor' r='10' />
    <chakra.circle cx='11' cy='6.75' fill={knockout} r='1.2' />
    <chakra.rect
      fill={knockout}
      height='6.5'
      rx='1'
      width='2'
      x='10'
      y='9.75'
    />
  </svg>
)

const variantGlyphs: Record<AlertVariant, ComponentType<GlyphProps>> = {
  success: SuccessGlyph,
  warning: WarningGlyph,
  error: ErrorGlyph,
  info: InfoGlyph,
}

// Compact callouts bind the design library's Alert *iconography* — outlined,
// stroke-only glyphs (Product · Trace Logs, nodes 7827:45 / 7828:37) — rather
// than the filled card glyphs above. Their designed colors (#8FB8DC info,
// #00C054 success) are exactly the status.info / status.success semantics the
// icon box already sets, so `currentColor` reproduces them in dark mode while
// staying theme-aware in light.
const InfoOutlineGlyph = ({ size = 16 }: GlyphProps) => (
  <svg fill='none' height={size} viewBox='0 0 18.6667 18.6667' width={size}>
    <path
      d='M9.33333 6V9.33333M9.33333 12.6667H9.34167M17.6667 9.33333C17.6667 13.9357 13.9357 17.6667 9.33333 17.6667C4.73096 17.6667 1 13.9357 1 9.33333C1 4.73096 4.73096 1 9.33333 1C13.9357 1 17.6667 4.73096 17.6667 9.33333Z'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='2'
    />
  </svg>
)

const SuccessOutlineGlyph = ({ size = 16 }: GlyphProps) => (
  <svg fill='none' height={size} viewBox='0 0 17.8333 17' width={size}>
    <path
      d='M6 7.66667L8.5 10.1667L16.8333 1.83333M16 8.5V14.3333C16 14.7754 15.8244 15.1993 15.5118 15.5118C15.1993 15.8244 14.7754 16 14.3333 16H2.66667C2.22464 16 1.80072 15.8244 1.48816 15.5118C1.17559 15.1993 1 14.7754 1 14.3333V2.66667C1 2.22464 1.17559 1.80072 1.48816 1.48816C1.80072 1.17559 2.22464 1 2.66667 1H11.8333'
      stroke='currentColor'
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth='2'
    />
  </svg>
)

// Warning and error keep the filled card glyphs until the design library
// publishes outlined variants for them.
const compactVariantGlyphs: Record<AlertVariant, ComponentType<GlyphProps>> = {
  success: SuccessOutlineGlyph,
  warning: WarningGlyph,
  error: ErrorGlyph,
  info: InfoOutlineGlyph,
}

/**
 * Flat tinted status alert. Renders a leading glyph, an emphasized title, and
 * optional supporting description.
 *
 * @see {@link https://www.figma.com/design/SkuYW7JEVMSUxnNC4lpTi4/Design-Library-%C2%B7-Foundation?node-id=884-1518&p=f&t=BF9CCCUD7n35ECPi-0 Design Library · Toasts & Alerts}
 */
export const Alert = ({
  variant,
  title,
  description,
  icon,
  colorScheme = 'dark',
  size = 'default',
  isCentered = false,
  primaryButton,
  onDismiss,
}: AlertProps) => {
  // Normalized once: an unrecognized variant would otherwise miss both the
  // token maps (TypeError) and the glyph map (invalid element type).
  const safeVariant = isAlertVariant(variant) ? variant : FALLBACK_ALERT_VARIANT
  const VariantGlyph =
    size === 'compact'
      ? compactVariantGlyphs[safeVariant]
      : variantGlyphs[safeVariant]

  return (
    <Box
      {...alertStyles(safeVariant, colorScheme, isCentered, size)}
      role='alert'
    >
      <Box
        aria-hidden='true'
        {...alertIconStyles(safeVariant, colorScheme, size)}
      >
        {icon ?? (
          <VariantGlyph
            knockout={knockoutColor[colorScheme]}
            size={ALERT_GLYPH_SIZE[size]}
          />
        )}
      </Box>
      <Box {...alertBodyStyles(isCentered)}>
        {title && <Text {...alertTitleStyles(colorScheme)}>{title}</Text>}
        {description && (
          <Text {...alertDescStyles(safeVariant, colorScheme, size)}>
            {description}
          </Text>
        )}
      </Box>
      {primaryButton && (
        <Box {...alertActionStyles}>
          <Button
            colorScheme={colorScheme}
            onClick={primaryButton.onClick}
            size='sm'
            variant='primary'
          >
            {primaryButton.text}
          </Button>
        </Box>
      )}
      {onDismiss && (
        <Box {...alertDismissStyles}>
          <UtilityButton
            colorScheme={colorScheme}
            icon={<CloseIcon />}
            label='Dismiss'
            onClick={onDismiss}
          />
        </Box>
      )}
    </Box>
  )
}
